import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
//from public demo data

// Menu Category Interface
export interface MenuCategory {
  id: string;
  name: string;
  icon: string;
  href: string;
}

// Menu Hero Stats Interface
export interface MenuHeroStats {
  value: string;
  label: string;
}

// Menu Hero Chef Image Interface
export interface MenuHeroChefImage {
  src: string;
  alt: string;
  caption: string;
}

// Menu Hero Interface
export interface MenuHero {
  eyebrow: string;
  title: {
    main: string;
    decorative: string;
    ending: string;
  };
  description: string;
  stats: MenuHeroStats[];
  chefImage: MenuHeroChefImage;
}

// Featured Dish Detail Interface
export interface FeaturedDishDetail {
  icon: string;
  text: string;
}

// Featured Dish Interface
export interface MenuFeaturedDish {
  badge: string;
  title: string;
  price: number;
  rating: number;
  reviewCount: number;
  description: string;
  details: FeaturedDishDetail[];
  imageSrc: string;
  imageAlt: string;
}

// Menu Item Interface
export interface MenuItem {
  id: string;
  title: string;
  description: string;
  price: number;
  imageSrc: string;
  imageAlt: string;
  rating: number;
  reviewCount: number;
}

// Menu Section Interface
export interface MenuSection {
  id: string;
  title: string;
  description: string;
  layout: "list" | "grid";
  items: MenuItem[];
}

// Order Summary Item Interface
export interface OrderSummaryItem {
  id: string;
  title: string;
  price: number;
  quantity: number;
}

// Complete Menu Data Interface
export interface MenuData {
  categories: MenuCategory[];
  hero: MenuHero;
  featuredDish: MenuFeaturedDish;
  sections: MenuSection[];
  orderSummary: OrderSummaryItem[];
}

// Menu State Interface
export interface MenuState {
  data: MenuData | null;
  loading: boolean;
  error: string | null;
  activeCategory: string | null;
  selectedItems: OrderSummaryItem[];
  totalPrice: number;
}

// Initial State
const initialState: MenuState = {
  data: {} as MenuData,
  loading: false,
  error: null,
  activeCategory: null,
  selectedItems: [],
  totalPrice: 0,
};

// Menu Slice
export const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    // Set menu data
    setMenuData: (state, action: PayloadAction<MenuData>) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },

    // Set loading state
    setMenuLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    // Set error state
    setMenuError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Set active category
    setActiveCategory: (state, action: PayloadAction<string | null>) => {
      state.activeCategory = action.payload;
    },

    // Add item to order
    addToOrder: (state, action: PayloadAction<OrderSummaryItem>) => {
      const existingItemIndex = state.selectedItems.findIndex(
        (item) => item.id === action.payload.id,
      );

      if (existingItemIndex >= 0) {
        // Update quantity if item already exists
        state.selectedItems[existingItemIndex].quantity +=
          action.payload.quantity;
      } else {
        // Add new item
        state.selectedItems.push(action.payload);
      }

      // Recalculate total price
      state.totalPrice = state.selectedItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0,
      );
    },

    // Remove item from order
    removeFromOrder: (state, action: PayloadAction<string>) => {
      state.selectedItems = state.selectedItems.filter(
        (item) => item.id !== action.payload,
      );

      // Recalculate total price
      state.totalPrice = state.selectedItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0,
      );
    },

    // Update item quantity
    updateItemQuantity: (
      state,
      action: PayloadAction<{ id: string; quantity: number }>,
    ) => {
      const itemIndex = state.selectedItems.findIndex(
        (item) => item.id === action.payload.id,
      );

      if (itemIndex >= 0) {
        if (action.payload.quantity <= 0) {
          // Remove item if quantity is 0 or less
          state.selectedItems.splice(itemIndex, 1);
        } else {
          // Update quantity
          state.selectedItems[itemIndex].quantity = action.payload.quantity;
        }

        // Recalculate total price
        state.totalPrice = state.selectedItems.reduce(
          (total, item) => total + item.price * item.quantity,
          0,
        );
      }
    },

    // Clear order
    clearOrder: (state) => {
      state.selectedItems = [];
      state.totalPrice = 0;
    },

    // Reset menu state
    resetMenuState: () => initialState,
  },
});

// Export actions
export const {
  setMenuData,
  setMenuLoading,
  setMenuError,
  setActiveCategory,
  addToOrder,
  removeFromOrder,
  updateItemQuantity,
  clearOrder,
  resetMenuState,
} = menuSlice.actions;

// Export reducer
export default menuSlice.reducer;
